import { getRequest } from '../api/ApiController';
import { CHECKLIST_TEMPLATE_INTENSITY_VALUES, parseSendingData } from './checklistTemplateSettingsList';
import { getServiceTaskHttpStatus } from './serviceTaskCreateHttp';
import { uiT } from './uiTranslate';

export const CHECKLIST_TEMPLATES_URL = '/api/checklist_templates';
export const CHECKLIST_TEMPLATE_LIST_PAGE = 'ChecklistTemplateList';
export const CHECKLIST_TEMPLATE_ADD_PAGE = 'ChecklistTemplateAdd';

export const CHECKLIST_TEMPLATE_WRITE_FIELDS = Object.freeze([
  'name',
  'description',
  'sending_data',
  'manager_id',
  'manager_due_days',
  'is_active',
  'machine_id',
  'recipients',
  'questions',
]);

export const CHECKLIST_TEMPLATE_NAME_MAX = 255;
export const CHECKLIST_TEMPLATE_DUE_DAYS_MIN = 1;
export const CHECKLIST_TEMPLATE_DUE_DAYS_MAX = 3650;

export const EMPTY_CHECKLIST_TEMPLATE_SENDING_DATA = Object.freeze({
  intensity: '',
  time: '',
  daysOfWeek: [],
  daysOfMonth: [],
  specialMonthlyDays: [],
  month: '',
  timesDaily: [],
  workdaysOnly: false,
  weekendHandling: 'before',
  dayMonth: '',
  times: [],
  notification_before: [],
});

let inFlight = null;
let pendingInvalidation = false;
let invalidationGeneration = 0;
let deleteBatchActive = false;

export function normalizeChecklistTemplateActorId(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  return s === '' || s === 'null' || s === 'undefined' ? null : s;
}

export function areChecklistTemplatesLoaded(state) {
  return state?.checklist_templates_loaded === true;
}

export function selectChecklistTemplates(state) {
  return Array.isArray(state?.checklist_templates) ? state.checklist_templates : [];
}

export function extractChecklistTemplateRows(response) {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return null;
}

export function reconstructChecklistTemplateUniverse(state) {
  const bases = Array.isArray(state?.checklist_templates) ? state.checklist_templates : [];
  const extras = (Array.isArray(state?.checklist_template_records) ? state.checklist_template_records : [])
    .filter((row) => row?.substitute_user_id != null);
  return [...bases, ...extras];
}

export function deriveChecklistTemplateCollections(allTemplates, user) {
  const list = Array.isArray(allTemplates) ? allTemplates : [];
  const userId = normalizeChecklistTemplateActorId(user?.id);
  const checklist_templates = list.filter((template) => template?.substitute_user_id == null);
  const checklist_template_records = list
    // Keep the historical picker contract: is_active == 1 (numeric or string).
    // eslint-disable-next-line eqeqeq
    .filter((template) => template?.is_active == 1)
    .filter((template) => {
      const assigned = Array.isArray(template?.users)
        && template.users.some((u) => normalizeChecklistTemplateActorId(u?.id) === userId);
      const substitute = normalizeChecklistTemplateActorId(template?.substitute_user_id) === userId;
      return assigned || substitute;
    });
  return { checklist_templates, checklist_template_records };
}

export function upsertCanonicalChecklistTemplateRow(allTemplates, row) {
  if (!row || row.id == null) return Array.isArray(allTemplates) ? allTemplates : [];
  const list = Array.isArray(allTemplates) ? [...allTemplates] : [];
  const id = normalizeChecklistTemplateActorId(row.id);
  let replaced = false;
  const next = list.map((item) => {
    if (normalizeChecklistTemplateActorId(item?.id) === id && item?.substitute_user_id == null) {
      replaced = true;
      return row;
    }
    return item;
  });
  if (!replaced) next.push(row);
  return next;
}

export function removeChecklistTemplateRowsByIds(allTemplates, ids) {
  const remove = new Set((Array.isArray(ids) ? ids : []).map(normalizeChecklistTemplateActorId).filter(Boolean));
  return (Array.isArray(allTemplates) ? allTemplates : [])
    .filter((item) => !remove.has(normalizeChecklistTemplateActorId(item?.id)));
}

export function applyChecklistTemplates(dispatch, rows) {
  dispatch({
    type: 'load_checklist_templates',
    newChecklistTemplates: Array.isArray(rows) ? rows : [],
  });
}

export function dispatchUpsertChecklistTemplate(dispatch, row) {
  dispatch({
    type: 'upsert_checklist_template',
    checklistTemplate: row,
  });
}

export function dispatchRemoveChecklistTemplates(dispatch, ids) {
  dispatch({
    type: 'remove_checklist_templates',
    ids: Array.isArray(ids) ? ids : [],
  });
}

export function hydrateChecklistTemplatesFromAuthPayload(dispatch, responseData) {
  if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData)) {
    return false;
  }
  if (!Object.prototype.hasOwnProperty.call(responseData, 'checklist_templates')) {
    return false;
  }
  const rows = Array.isArray(responseData.checklist_templates) ? responseData.checklist_templates : [];
  applyChecklistTemplates(dispatch, rows);
  return true;
}

function bumpChecklistTemplateInvalidation() {
  pendingInvalidation = true;
  invalidationGeneration += 1;
}

export function refreshChecklistTemplates({ dispatch, request = getRequest } = {}) {
  if (inFlight) return inFlight;
  const startedGeneration = invalidationGeneration;
  try {
    inFlight = Promise.resolve(request(CHECKLIST_TEMPLATES_URL))
      .then((response) => {
        const rows = extractChecklistTemplateRows(response);
        if (rows == null) {
          throw new Error('Unable to load checklist templates');
        }
        applyChecklistTemplates(dispatch, rows);
        if (startedGeneration === invalidationGeneration) {
          pendingInvalidation = false;
        }
        return rows;
      })
      .finally(() => {
        inFlight = null;
      });
  } catch (error) {
    inFlight = null;
    return Promise.reject(error);
  }
  return inFlight;
}

export function ensureChecklistTemplatesLoaded({ dispatch, state, request = getRequest } = {}) {
  if (areChecklistTemplatesLoaded(state)) {
    return Promise.resolve(selectChecklistTemplates(state));
  }
  return refreshChecklistTemplates({ dispatch, request });
}

export const CHECKLIST_TEMPLATE_PUSHER_EVENT = 'isg-event';
export const CHECKLIST_TEMPLATE_PUSHER_CHANNEL = 'isg-channel';

export function parseChecklistTemplateListPusherNotification(notification) {
  let inner = notification?.data?.data ?? notification?.data;
  if (typeof inner === 'string') {
    try {
      inner = JSON.parse(inner);
    } catch {
      inner = null;
    }
  }
  if (!inner || typeof inner !== 'object' || Array.isArray(inner)) {
    inner = {};
  }
  return {
    eventName: CHECKLIST_TEMPLATE_PUSHER_EVENT,
    page: inner.page ?? null,
    templateId: inner.id ?? null,
    module: inner.module ?? null,
    actorUserId: notification?.user_id ?? notification?.data?.user_id ?? null,
    operation: inner.operation ?? null,
    socketId: notification?.socket_id ?? inner.socket_id ?? null,
    origin: inner.origin ?? null,
    correlationId: inner.correlation_id ?? inner.correlationId ?? null,
  };
}

export function checklistTemplatePusherHasMutationCorrelation(parsed) {
  return Boolean(parsed?.socketId || parsed?.origin || parsed?.correlationId);
}

export function canInvalidateChecklistTemplateList(user) {
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  return perms.includes('access checklists-setup')
    || perms.includes('access checklists')
    || perms.includes('edit checklists');
}

export function resetChecklistTemplatesLookup() {
  inFlight = null;
  pendingInvalidation = false;
  invalidationGeneration = 0;
  deleteBatchActive = false;
}

export function resetChecklistTemplatesLookupForTests() {
  resetChecklistTemplatesLookup();
}

export function selectedChecklistWorkspacePage(state) {
  const selected = (state?.panels || []).find((panel) => String(panel?.value) === String(state?.selectedTab));
  return selected?.child || null;
}

export function isChecklistTemplateListVisible(state) {
  return selectedChecklistWorkspacePage(state) === CHECKLIST_TEMPLATE_LIST_PAGE;
}

export function hasChecklistTemplatePendingInvalidation() {
  return pendingInvalidation === true;
}

export function resolveChecklistTemplatePusherAction(state) {
  if (deleteBatchActive) {
    return { action: 'coalesce_delete_batch', get: 0, pending: true };
  }
  if (isChecklistTemplateListVisible(state)) {
    return { action: 'force_refresh', get: 1 };
  }
  return { action: 'pending_hidden', get: 0, pending: true };
}

export function beginChecklistTemplateDeleteBatch() {
  deleteBatchActive = true;
}

export function endChecklistTemplateDeleteBatch({ dispatch, state, user, request = getRequest } = {}) {
  deleteBatchActive = false;
  if (user != null && !canInvalidateChecklistTemplateList(user)) {
    return Promise.resolve({ action: 'skip_unauthorized', get: 0 });
  }
  if (!pendingInvalidation) {
    return Promise.resolve({ action: 'delete_batch_no_pusher', get: 0 });
  }
  if (!isChecklistTemplateListVisible(state)) {
    return Promise.resolve({ action: 'delete_batch_pending_hidden', get: 0 });
  }
  return refreshChecklistTemplates({ dispatch, request }).then((rows) => ({
    action: 'delete_batch_refresh',
    get: 1,
    rows,
  }));
}

export function flushChecklistTemplatePendingInvalidation({ dispatch, state, user, request = getRequest } = {}) {
  if (user != null && !canInvalidateChecklistTemplateList(user)) {
    return Promise.resolve({ action: 'skip_unauthorized', get: 0 });
  }
  if (!pendingInvalidation) {
    return Promise.resolve({ action: 'no_pending', get: 0 });
  }
  if (!isChecklistTemplateListVisible(state)) {
    return Promise.resolve({ action: 'still_hidden', get: 0 });
  }
  return refreshChecklistTemplates({ dispatch, request }).then((rows) => ({
    action: 'flush_pending',
    get: 1,
    rows,
  }));
}

export function syncChecklistTemplatesFromPusher({ dispatch, state, user, request = getRequest } = {}) {
  if (user != null && !canInvalidateChecklistTemplateList(user)) {
    return Promise.resolve({ action: 'skip_unauthorized', get: 0 });
  }
  const decision = resolveChecklistTemplatePusherAction(state);
  bumpChecklistTemplateInvalidation();
  if (decision.get !== 1) {
    return Promise.resolve(decision);
  }
  return refreshChecklistTemplates({ dispatch, request }).then((rows) => ({ ...decision, rows }));
}

export function extractChecklistTemplateWritePayload(response) {
  if (!response || typeof response !== 'object' || Array.isArray(response)) {
    return { row: null, changed: undefined };
  }
  const changed = response.changed;
  const data = response.data;
  if (Array.isArray(data)) {
    return { row: null, changed };
  }
  if (data && typeof data === 'object' && data.id != null) {
    return { row: data, changed };
  }
  if (response.id != null) {
    return { row: response, changed };
  }
  return { row: null, changed };
}

export function isChecklistTemplateWriteOk(response) {
  const http = getServiceTaskHttpStatus(response);
  if (http != null && (http < 200 || http >= 300)) return false;
  const status = Number(response?.status);
  return status === 200 || status === 201;
}

export function formikErrorsFromLaravel(errors) {
  const out = {};
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return out;
  Object.entries(errors).forEach(([key, value]) => {
    const message = Array.isArray(value) ? value[0] : value;
    if (message) out[key] = String(message);
  });
  return out;
}

export function interpretChecklistTemplateWriteResponse(response) {
  const http = getServiceTaskHttpStatus(response);
  const bodyStatus = Number(response?.status);
  if (isChecklistTemplateWriteOk(response)) {
    return { ok: true, kind: 'success', httpStatus: http ?? bodyStatus };
  }
  const status = http ?? (Number.isFinite(bodyStatus) ? bodyStatus : null);
  const fieldErrors = formikErrorsFromLaravel(response?.errors);
  if (status === 422 || Object.keys(fieldErrors).length > 0) {
    return {
      ok: false,
      kind: 'validation',
      httpStatus: status || 422,
      fieldErrors,
      message: response?.message ? String(response.message) : null,
    };
  }
  if (status === 403 || status === 419 || status === 401) {
    return { ok: false, kind: 'permission', httpStatus: status, fieldErrors: {}, message: response?.message ? String(response.message) : null };
  }
  if (response == null || typeof response !== 'object' || Array.isArray(response)) {
    return { ok: false, kind: 'invalid_body', httpStatus: status, fieldErrors: {}, message: null };
  }
  return { ok: false, kind: 'server', httpStatus: status, fieldErrors: {}, message: response?.message ? String(response.message) : null };
}

export function validateChecklistTemplateFormValues(values) {
  const errors = {};
  const name = String(values?.name ?? '').trim();
  if (!name) errors.name = uiT('Common.Required');
  else if (name.length > CHECKLIST_TEMPLATE_NAME_MAX) errors.name = uiT('Common.Required');
  const due = values?.manager_due_days;
  if (due === '' || due == null) {
    errors.manager_due_days = uiT('Common.Required');
  } else {
    const asNumber = Number(due);
    if (!Number.isInteger(asNumber) || asNumber < CHECKLIST_TEMPLATE_DUE_DAYS_MIN || asNumber > CHECKLIST_TEMPLATE_DUE_DAYS_MAX) {
      errors.manager_due_days = uiT('ChecklistTemplate.Validation.IntegerRange');
    }
  }
  if (!['0', '1', 0, 1].map(String).includes(String(values?.is_active))) {
    errors.is_active = uiT('Common.Required');
  }
  const intensity = values?.sending_data?.intensity;
  if (intensity && !CHECKLIST_TEMPLATE_INTENSITY_VALUES.includes(intensity)) {
    errors['sending_data.intensity'] = uiT('ChecklistTemplate.Validation.InvalidValue');
  }
  return errors;
}

function objectId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') return value.id ?? '';
  return value;
}

function parseQuestionAnswerModes(raw) {
  if (raw == null || raw === '') return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function formSendingDataFromTemplate(raw) {
  return { ...EMPTY_CHECKLIST_TEMPLATE_SENDING_DATA, ...parseSendingData(raw) };
}

function removeDiacritics(str) {
  return String(str ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function extractDepartmentsFromRecipients(recipients) {
  const departmentMap = new Map();
  (Array.isArray(recipients) ? recipients : []).forEach((user) => {
    const dept = user?.employee?.department;
    if (dept && !departmentMap.has(dept.id)) {
      departmentMap.set(dept.id, {
        id: dept.id,
        name: dept.name,
        searchLabel: removeDiacritics(dept.name).toLowerCase(),
      });
    }
  });
  return Array.from(departmentMap.values());
}

export function canonicalChecklistTemplateToFormValues(template) {
  if (!template) {
    return {
      name: '',
      description: '',
      recipientDepartments: [],
      recipients: [],
      sending_data: { ...EMPTY_CHECKLIST_TEMPLATE_SENDING_DATA },
      questions: [],
      manager_id: null,
      manager_due_days: '3',
      is_active: '1',
      machine_id: '',
    };
  }
  const managerName = template.manager?.employee?.name || template.manager?.username || '';
  const machineName = template.machine?.name || '';
  return {
    id: template.id,
    name: template.name ?? '',
    description: template.description ?? '',
    recipientDepartments: extractDepartmentsFromRecipients(template.users || []).map((department) => ({
      name: department.name,
      id: department.id,
    })),
    recipients: (template.users || []).map((user) => ({
      name: user?.employee?.name || user?.username,
      id: user.id,
      searchLabel: removeDiacritics(user?.employee?.name || user?.username).toLowerCase(),
    })),
    sending_data: formSendingDataFromTemplate(template.sending_data),
    questions: (Array.isArray(template.questions) ? template.questions : []).map((q) => {
      const options = parseQuestionAnswerModes(q?.answer_modes);
      return {
        question: q?.text ?? q?.question ?? '',
        options: {
          okNok: options?.okNok ?? false,
          okNote: options?.okNote ?? false,
          nokNote: options?.nokNote ?? false,
          no: options?.no ?? false,
          noNote: options?.noNote ?? false,
          photo: options?.photo ?? false,
          optional_photo: options?.optional_photo ?? false,
          name: options?.name ?? false,
          priorityDepartments: options?.priorityDepartments ?? [],
        },
      };
    }),
    manager_id: template.manager
      ? {
        name: managerName,
        id: template.manager.id,
        searchLabel: removeDiacritics(managerName).toLowerCase(),
      }
      : null,
    manager_due_days: template.manager_due_days ?? '',
    is_active: `${template.is_active ?? ''}`,
    machine_id: {
      name: machineName,
      id: template.machine?.id || '',
      searchLabel: removeDiacritics(machineName).toLowerCase(),
    },
  };
}

export function appendChecklistTemplateWriteFormData(formData, values) {
  const payload = values && typeof values === 'object' ? values : {};
  formData.append('name', payload.name ?? '');
  formData.append('description', payload.description ?? '');
  formData.append('sending_data', JSON.stringify(payload.sending_data ?? {}));
  formData.append('manager_id', objectId(payload.manager_id));
  formData.append('manager_due_days', payload.manager_due_days ?? '');
  formData.append('is_active', payload.is_active ?? '');
  formData.append('machine_id', objectId(payload.machine_id));
  const recipientIds = (Array.isArray(payload.recipients) ? payload.recipients : [])
    .map((item) => (item && typeof item === 'object' ? item.id : item))
    .filter((id) => id != null && id !== '');
  formData.append('recipients', recipientIds);
  formData.append('questions', JSON.stringify(Array.isArray(payload.questions) ? payload.questions : []));
  return formData;
}

export function formDataKeys(formData) {
  return Array.from(formData.keys());
}
